import { createSlice } from '@reduxjs/toolkit';
import tripLegInventoryService from '../services/tripLegInventoryService';
import * as uiSlice from './ui.slice';
import type { TripLegPackageRejectReason } from './models/trip-leg-package-reject-reason.model';
import {
  TripLegStatusBC,
  TripLegStatusReference,
} from './models/trip-leg-status.model';
import referenceService from '../services/referenceService';
import type { AppDispatch, RootState } from './store';
import {
  TripLegPackagesResponse,
  TripLegPackage,
} from './models/trip-leg-package.model';
import { TripLegPackagesRequest } from './requests/trip-legs-packages';

interface TripInventoryState {
  offeredPackages: TripLegPackage[];
  loadingOfferedPackages: boolean;
  loadedOfferedPackages: boolean;
  tripLegPackage: TripLegPackage | null;
  loadingTripLegPackage: boolean;
  loadedTripLegPackage: boolean;
  acceptingTripPackage: boolean;
  acceptedTripPackage: boolean;
  rejectingTripPackage: boolean;
  rejectedTripPackage: boolean;
  tripLegPackageRejectReasons: TripLegPackageRejectReason[];
  offeredTripLegEvent: boolean;
}

const finalizedStatusBCs: Set<string> = new Set([
  TripLegStatusBC.Canceled,
  TripLegStatusBC.CanceledLate,
  TripLegStatusBC.Completed,
  TripLegStatusBC.NoLoad,
  TripLegStatusBC.Denied,
  TripLegStatusBC.Abandoned,
  TripLegStatusBC.Void,
]);

const getFinalizedStatusIDs = (
  tripLegStatuses: TripLegStatusReference[],
): Set<number> =>
  new Set(
    tripLegStatuses
      .filter((s) => finalizedStatusBCs.has(s.tripLegStatusBC.toUpperCase()))
      .map((s) => s.tripLegStatusID),
  );

export const initialState: TripInventoryState = {
  offeredPackages: [],
  loadingOfferedPackages: false,
  loadedOfferedPackages: false,
  tripLegPackage: null,
  loadingTripLegPackage: false,
  loadedTripLegPackage: false,
  acceptingTripPackage: false,
  acceptedTripPackage: false,
  rejectingTripPackage: false,
  rejectedTripPackage: false,
  tripLegPackageRejectReasons: [],
  offeredTripLegEvent: false,
};

export const slice = createSlice({
  name: 'notification',
  initialState,
  reducers: {
    setOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackage[] },
    ) => {
      state.offeredPackages = action.payload;
    },
    setLoadingOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingOfferedPackages = action.payload;
    },
    setLoadedOfferedPackages: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedOfferedPackages = action.payload;
    },
    upsertPackage: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackage },
    ) => {
      const idx = state.offeredPackages.findIndex(
        (p) => p.tripLegPackageUUID === action.payload.tripLegPackageUUID,
      );
      const pkg = action.payload;
      if (idx >= 0) {
        state.offeredPackages[idx] = pkg;
      } else {
        state.offeredPackages.push(pkg);
      }
    },
    removePackage: (
      state: TripInventoryState,
      action: { type: string; payload: string },
    ) => {
      const packages = [...state.offeredPackages];
      const idx = packages.findIndex(
        (p) => p.tripLegPackageUUID === action.payload,
      );
      if (idx >= 0) {
        packages.splice(idx, 1);
        state.offeredPackages = packages;
      }
    },
    removeTripLegFromPackagesByReferenceId: (
      state: TripInventoryState,
      action: { type: string; payload: string },
    ) => {
      const tripLegReferenceID = action.payload;
      state.offeredPackages = state.offeredPackages
        .map((pkg) => ({
          ...pkg,
          tripLegs: pkg.tripLegs.filter(
            (leg) => leg.tripLegReferenceID !== tripLegReferenceID,
          ),
        }))
        .filter((pkg) => pkg.tripLegs.length > 0);
      if (
        state.tripLegPackage?.tripLegs.some(
          (leg) => leg.tripLegReferenceID === tripLegReferenceID,
        )
      ) {
        const updatedLegs = state.tripLegPackage.tripLegs.filter(
          (leg) => leg.tripLegReferenceID !== tripLegReferenceID,
        );
        state.tripLegPackage =
          updatedLegs.length > 0
            ? { ...state.tripLegPackage, tripLegs: updatedLegs }
            : null;
      }
    },
    setTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackage | null },
    ) => {
      state.tripLegPackage = action.payload;
    },
    setLoadingTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegPackage = action.payload;
    },
    setLoadedTripLegPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegPackage = action.payload;
    },
    setAcceptingTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.acceptingTripPackage = action.payload;
    },
    setAcceptedTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.acceptedTripPackage = action.payload;
    },
    setRejectingTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.rejectingTripPackage = action.payload;
    },
    setRejectedTripPackage: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.rejectedTripPackage = action.payload;
    },
    setTripLegPackageRejectReasons: (
      state: TripInventoryState,
      action: { type: string; payload: TripLegPackageRejectReason[] },
    ) => {
      state.tripLegPackageRejectReasons = action.payload;
    },
    setOfferedTripLegEvent: (
      state: TripInventoryState,
      action: { type: string; payload: boolean },
    ) => {
      state.offeredTripLegEvent = action.payload;
    },
  },
});

export const { setOfferedPackages } = slice.actions;
export const { setLoadingOfferedPackages } = slice.actions;
export const { setLoadedOfferedPackages } = slice.actions;
export const { removePackage } = slice.actions;
export const { removeTripLegFromPackagesByReferenceId } = slice.actions;
export const { upsertPackage } = slice.actions;
export const { setTripLegPackage } = slice.actions;
export const { setLoadingTripLegPackage } = slice.actions;
export const { setLoadedTripLegPackage } = slice.actions;

export const { setAcceptingTripPackage } = slice.actions;
export const { setAcceptedTripPackage } = slice.actions;
export const { setRejectingTripPackage } = slice.actions;
export const { setRejectedTripPackage } = slice.actions;
export const { setTripLegPackageRejectReasons } = slice.actions;

export const { setOfferedTripLegEvent } = slice.actions;

export const addPackage =
  (detail: TripLegPackage) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const finalizedStatusIDs = getFinalizedStatusIDs(
      getState().reference.tripLegStatuses,
    );
    const filteredLegs =
      detail.tripLegs?.filter(
        (leg) => !finalizedStatusIDs.has(leg.tripLegStatusID),
      ) ?? [];
    if (filteredLegs.length === 0) {
      dispatch(removePackage(detail.tripLegPackageUUID));
      return;
    }
    dispatch(upsertPackage({ ...detail, tripLegs: filteredLegs }));
  };

export const fetchAndAddOfferedPackageAsync =
  (transportationProviderID: number, packageUUID: string) =>
  (dispatch: AppDispatch) => {
    tripLegInventoryService
      .getTripLegPackage(transportationProviderID, packageUUID)
      .subscribe({
        next: (tripLegPackage: TripLegPackage) => {
          dispatch(addPackage(tripLegPackage));
        },
        error: () => {},
      });
  };

export const getOfferedPackagesAsync =
  (request: TripLegPackagesRequest) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoadingOfferedPackages(true));
    dispatch(setLoadedOfferedPackages(false));
    dispatch(setOfferedPackages([]));
    tripLegInventoryService.getOfferedPackages(request).subscribe({
      next: (response: TripLegPackagesResponse) => {
        dispatch(setLoadingOfferedPackages(false));
        dispatch(setLoadedOfferedPackages(true));
        const finalizedStatusIDs = getFinalizedStatusIDs(
          getState().reference.tripLegStatuses,
        );

        const packagesWithLegs = (response.items || [])
          .map(({ tripLegs, ...pkg }) => ({
            ...pkg,
            tripLegs:
              tripLegs?.filter(
                (leg) => !finalizedStatusIDs.has(leg.tripLegStatusID),
              ) ?? [],
          }))
          .filter((pkg) => pkg.tripLegs.length > 0);
        dispatch(
          setOfferedPackages(
            packagesWithLegs.sort((a, b) =>
              a.tripLegPackageOfferingExpirationDateTimeUTC.localeCompare(
                b.tripLegPackageOfferingExpirationDateTimeUTC,
              ),
            ),
          ),
        );
      },
      error: (err) => {
        dispatch(setLoadingOfferedPackages(false));
        dispatch(setLoadedOfferedPackages(false));
      },
    });
  };

export const getTripLegPackageAsync =
  (providerID: number, packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegPackage(true));
    dispatch(setLoadedTripLegPackage(false));
    tripLegInventoryService
      .getTripLegPackage(providerID, packageUUID)
      .subscribe({
        next: (tripLegPackage: TripLegPackage) => {
          dispatch(setLoadingTripLegPackage(false));
          dispatch(setLoadedTripLegPackage(true));
          dispatch(setTripLegPackage(tripLegPackage));
        },
        error: (err) => {
          dispatch(setLoadingTripLegPackage(false));
          dispatch(setLoadedTripLegPackage(false));
          dispatch(setTripLegPackage(null));
        },
      });
  };

export const acceptTripPackageAsync =
  (packageUUID: string) => (dispatch: AppDispatch) => {
    dispatch(setAcceptingTripPackage(true));
    dispatch(setAcceptedTripPackage(false));
    tripLegInventoryService.acceptTripPackage(packageUUID).subscribe({
      next: () => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(true));
        dispatch(removePackage(packageUUID));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.acceptTripPackageSuccessMessage'),
        );
      },
      error: (err) => {
        dispatch(setAcceptingTripPackage(false));
        dispatch(setAcceptedTripPackage(false));
        dispatch(
          uiSlice.setErrorMessage('snackbar.acceptTripPackageFailureMessage'),
        );
      },
    });
  };

export const rejectTripPackageAsync =
  (packageUUID: string, tripLegPackageStatusReasonBC: string) =>
  (dispatch: AppDispatch) => {
    dispatch(setRejectingTripPackage(true));
    dispatch(setRejectedTripPackage(false));
    tripLegInventoryService
      .rejectTripPackage(packageUUID, tripLegPackageStatusReasonBC)
      .subscribe({
        next: () => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(true));
          dispatch(removePackage(packageUUID));
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.rejectTripPackageSuccessMessage',
            ),
          );
        },
        error: (err) => {
          dispatch(setRejectingTripPackage(false));
          dispatch(setRejectedTripPackage(false));
          dispatch(
            uiSlice.setErrorMessage('snackbar.rejectTripPackageFailureMessage'),
          );
        },
      });
  };

export const fetchTripLegPackageRejectReasonsAsync =
  () => (dispatch: AppDispatch) => {
    referenceService.getTripLegPackageRejectReasons().subscribe({
      next: (reasons) => {
        dispatch(setTripLegPackageRejectReasons(reasons));
      },
    });
  };

export const selectOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.offeredPackages;
export const selectLoadingOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadingOfferedPackages;
export const selectLoadedOfferedPackages = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadedOfferedPackages;
export const selectTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripLegPackage;
export const selectLoadingTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadingTripLegPackage;
export const selectLoadedTripLegPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.loadedTripLegPackage;
export const selectAcceptingTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.acceptingTripPackage;
export const selectAcceptedTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.acceptedTripPackage;
export const selectRejectingTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.rejectingTripPackage;
export const selectRejectedTripPackage = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.rejectedTripPackage;
export const selectTripLegPackageRejectReasons = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.tripLegPackageRejectReasons;

export const selectOfferedTripLegEvent = (state: {
  tripPackageInventory: TripInventoryState;
}) => state.tripPackageInventory.offeredTripLegEvent;

export default slice.reducer;
